/*!
 * Copyright 2019 Fondazione Bruno Kessler
 * Licensed under the EUPL, Version 1.1 or - as soon they will be approved by the European
 * Commission - subsequent versions of the EUPL (the "Licence"); You may not use this work
 * except in compliance with the Licence.
 *
 * You may obtain a copy of the Licence at:
 * http://ec.europa.eu/idabc/eupl.html
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the Licence is distributed on an "AS IS" basis, WITHOUT WARRANTIES OR CONDITIONS OF
 * ANY KIND, either express or implied. See the Licence for the specific language
 * governing permissions and limitations under the Licence.
 */

import {AgGridModule} from "@ag-grid-community/angular";
import {CommonModule} from "@angular/common";
import {NgModule} from "@angular/core";
//import { FlexLayoutModule } from "@angular/flex-layout";
import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import {MatAutocompleteModule} from "@angular/material/autocomplete";
import {MatButtonModule} from "@angular/material/button";
import {MatButtonToggleModule} from "@angular/material/button-toggle";
import {MatCheckboxModule} from "@angular/material/checkbox";
import {MatDatepickerModule} from "@angular/material/datepicker";
import {MatDialogModule} from "@angular/material/dialog";
import {MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldModule} from "@angular/material/form-field";
import {MatIconModule} from "@angular/material/icon";
import {MatInputModule} from "@angular/material/input";
import {MatListModule} from "@angular/material/list";
import {MatPaginatorModule} from "@angular/material/paginator";
import {MatProgressSpinnerModule} from "@angular/material/progress-spinner";
import {MatRadioModule} from "@angular/material/radio";
import {MatSelectModule} from "@angular/material/select";
import {MatSortModule} from "@angular/material/sort";
import {MatTableModule} from "@angular/material/table";
import {MatTooltipModule} from "@angular/material/tooltip";
import {RouterModule} from "@angular/router";
import {IonicModule} from "@ionic/angular";
import {ChartsModule} from "@modules/charts/charts.module";
import {DoctorSharedModule} from "@modules/doctor-panel/modules/doctor-shared/doctor-shared.module";
import {QuestionnaireTemplatesState} from "@modules/questionnaires/store/questionnaire-templates.state";
import {NgxsModule} from "@ngxs/store";
import {SharedFormUtilsModule} from "@shared/shared-form-utils/shared-form-utils.module";
import {SharedLayoutModule} from "@shared/shared-layout/shared-layout.module";
import {SharedNotYetImplModule} from "@shared/shared-not-yet-impl/shared-not-yet-impl.module";
import {SharedModule} from "@shared/shared/shared.module";
import {TrecClinicalDataModule} from "@trec/api/clinical-data";
import {C3DictionarySharedModule} from "@trec/app-modules/dictionary-shared";
import {C3AutocompleteModule} from "@trec/components/autocomplete";
import {C3DarkModeModule} from "@trec/components/dark-mode";
import {C3FormUtilsModule} from "@trec/components/form-utils";
import {C3GetIconModule} from "@trec/components/get-icon";
import {C3HoverUtilsModule} from "@trec/components/hover-utils";
import {C3MessageModule} from "@trec/components/message";
import {C3SnackbarStatusModule} from "@trec/components/snackbar-status";
import {C3TooltipModule} from "@trec/components/tooltip";
import {AppPrescrSharedModule} from "../app-prescr-shared/app-prescr-shared.module";
import {ConfigAppFeaturesComponent} from "./components/configs/config-app-features/config-app-features.component";
import {ConfigDiarioAnamnesiComponent} from "./components/configs/config-diario-anamnesi/config-diario-anamnesi.component";
import {ConfigDietComponent} from "./components/configs/config-diet/config-diet.component";
import {ConfigEduOndemandComponent} from "./components/configs/config-edu-ondemand/config-edu-ondemand.component";
import {ConfigExaminationsComponent} from "./components/configs/config-examinations/config-examinations.component";
import {ConfigGlycaemiaProfileComponent} from "./components/configs/config-glycaemia-profile/config-glycaemia-profile.component";
import {ConfigGoalsComponent} from "./components/configs/config-goals/config-goals.component";
import {ConfigIcd9Component} from "./components/configs/config-icd9/config-icd9.component";
import {ConfigMicrolearningComponent} from "./components/configs/config-microlearning/config-microlearning.component";
import {ConfigOperationsComponent} from "./components/configs/config-operations/config-operations.component";
import {ConfigPdtaComponent} from "./components/configs/config-pdta/config-pdta.component";
import {DialogPrescriptionPdtaComponent} from "./components/configs/config-pdta/dialog-prescription-pdta/dialog-prescription-pdta.component";
import {ConfigPersonalExperimentsComponent} from "./components/configs/config-personal-experiments/config-personal-experiments.component";
import {ConfigPhysicalActivityComponent} from "./components/configs/config-physical-activity/config-physical-activity.component";
import {ConfigPredictiveModelsComponent} from "./components/configs/config-predictive-models/config-predictive-models.component";
import {ConfigQuestionnairesComponent} from "./components/configs/config-questionnaires/config-questionnaires.component";
import {ConfigReportsComponent} from "./components/configs/config-reports/config-reports.component";
import {ConfigSectionBoxComponent} from "./components/configs/config-section-box/config-section-box.component";
import {ConfigSurveysComponent} from "./components/configs/config-surveys/config-surveys.component";
import {ConfigTasksComponent} from "./components/configs/config-tasks/config-tasks.component";
import {ConfigTeleconsultoComponent} from "./components/configs/config-teleconsulto/config-teleconsulto.component";
import {ConfigTelemonitoringComponent} from "./components/configs/config-telemonitoring/config-telemonitoring.component";
import {ConfigTherapiesComponent} from "./components/configs/config-therapies/config-therapies.component";
import {ConfigWarningsPatientComponent} from "./components/configs/config-warnings-patient/config-warnings-patient.component";
import {EditExaminationDialogComponent} from "./components/edit/edit-examination-dialog/edit-examination-dialog.component";
import {EditIcd9DialogComponent} from "./components/edit/edit-icd9-dialog/edit-icd9-dialog.component";
import {EditOperationDialogComponent} from "./components/edit/edit-operation-dialog/edit-operation-dialog.component";
import {EditTaskDialogComponent} from "./components/edit/edit-task-dialog/edit-task-dialog.component";
import {EditTherapyDialogComponent} from "./components/edit/edit-therapy-dialog/edit-therapy-dialog.component";
import {FormExaminationComponent} from "./components/forms/form-examination/form-examination.component";
import {FormTaskComponent} from "./components/forms/form-task/form-task.component";
import {FormTherapyOncologicalComponent} from "./components/forms/form-therapy-oncological/form-therapy-oncological.component";
import {FormTherapyComponent} from "./components/forms/form-therapy/form-therapy.component";
import {DrugSelectorComponent} from "./components/others/drug-selector/drug-selector.component";
import {FrequencySelectorComponent} from "./components/others/frequency-selector/frequency-selector.component";
import {TaskDeleteDialogComponent} from "./components/others/task-delete-dialog/task-delete-dialog.component";
import {TherapyTemplateSelectorComponent} from "./components/others/therapy-template-selector/therapy-template-selector.component";
import {TherapyVariableAssumptionsTableComponent} from "./components/others/therapy-variable-assumptions-table/therapy-variable-assumptions-table.component";
import {ConfigFormDirective} from "./directives/config-form.directive";
import {MonitoringPage} from "./pages/monitoring/monitoring.page";
import {TpAdvancedPage} from "./pages/tp-advanced/tp-advanced.page";
import {TpBasePage} from "./pages/tp-base/tp-base.page";
import {TreatmentPlanPage} from "./pages/treatment-plan/treatment-plan.page";
import {PatientTreatmentPlanRoutingModule} from "./patient-treatment-plan.routing";
import {ToAbsDayPipe} from "./pipes/to-abs-day.pipe";
import {PatientTreatmentPlanAdvState} from "./store/patient-treatment-plan-adv.state";
import {PatientTreatmentPlanBaseState} from "./store/patient-treatment-plan-base.state";
import {PatientTreatmentPlanMonState} from "./store/patient-treatment-plan-mon.state";


@NgModule({
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    DoctorSharedModule,
    ChartsModule,

    SharedModule,
    SharedLayoutModule,
    SharedNotYetImplModule,
    SharedFormUtilsModule,

    PatientTreatmentPlanRoutingModule,

    IonicModule,

    AppPrescrSharedModule,

    NgxsModule.forFeature([
      PatientTreatmentPlanBaseState,
      PatientTreatmentPlanAdvState,
      PatientTreatmentPlanMonState,
      QuestionnaireTemplatesState
    ]),

    AgGridModule,

    //FlexLayoutModule,
    MatListModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatCheckboxModule,
    MatButtonToggleModule,
    MatSelectModule,
    MatRadioModule,
    MatAutocompleteModule,
    MatDialogModule,
    MatTooltipModule,

    C3SnackbarStatusModule,
    C3MessageModule,
    C3GetIconModule,
    C3TooltipModule,
    C3DarkModeModule,
    TrecClinicalDataModule,

    C3DictionarySharedModule,
    C3FormUtilsModule,
    C3AutocompleteModule,
    C3HoverUtilsModule,


    ConfigQuestionnairesComponent,
  ],
  declarations: [
    // Pages
    TreatmentPlanPage,

    TpBasePage,
    TpAdvancedPage,
    MonitoringPage,

    // Config base
    ConfigTherapiesComponent,
    ConfigGlycaemiaProfileComponent,
    ConfigExaminationsComponent,
    ConfigTasksComponent,
    ConfigDietComponent,
    ConfigIcd9Component,
    ConfigPhysicalActivityComponent,

    // Config adv
    ConfigAppFeaturesComponent,
    ConfigSurveysComponent,
    ConfigPersonalExperimentsComponent,
    ConfigGoalsComponent,
    ConfigMicrolearningComponent,
    ConfigPredictiveModelsComponent,
    ConfigEduOndemandComponent,
    ConfigWarningsPatientComponent,

    // Monitoring
    ConfigTelemonitoringComponent,
    ConfigReportsComponent,
    ConfigOperationsComponent,
    // ConfigWarningsDoctorComponent,

    // Dialogs
    EditTherapyDialogComponent,
    EditExaminationDialogComponent,
    EditTaskDialogComponent,
    EditOperationDialogComponent,

    // Forms
    FormTherapyComponent,
    FormExaminationComponent,
    FormTaskComponent,
    FormTherapyOncologicalComponent,

    // Others
    ConfigSectionBoxComponent,

    FrequencySelectorComponent,
    DrugSelectorComponent,
    TherapyVariableAssumptionsTableComponent,
    TherapyTemplateSelectorComponent,

    TaskDeleteDialogComponent,

    // Directives
    ConfigFormDirective,

    // Pipes
    ToAbsDayPipe,
    ConfigPdtaComponent,
    ConfigDiarioAnamnesiComponent,
    DialogPrescriptionPdtaComponent,
    ConfigIcd9Component,
    ConfigTeleconsultoComponent,
    EditIcd9DialogComponent,
  ],
  providers: [
    // Styling parameters
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {appearance: "outline", floatLabel: "always"},
    },
  ],
  exports: [TreatmentPlanPage, FormTherapyComponent],
})
export class PatientTreatmentPlanModule { }
